import type {
  FilterParams,
  FilterParamsOffers,
  SearchParams,
  SortParams,
} from '@/modules/common/context/QueryParams/models';

import {
  convertFilterParamsBackendToParamsString,
  convertHotelFilterParamsToBackend,
  convertOfferFilterParamsToBackend,
  convertOtherParamsBackendToParamsString,
  convertSearchParamsBackendToParamsString,
  convertSearchParamsToBackend,
  convertSortParamsBackendToParamsString,
  convertSortParamsToBackend,
} from '@/modules/common/utils/convert';

export const combineHotelParamsToBackendQuery = ({
  searchParams,
  filterParams = null,
  sortParams = null,
  otherParams = null,
}: {
  searchParams: SearchParams;
  filterParams?: null | FilterParams;
  sortParams?: null | SortParams;
  otherParams?: null | {
    offset?: number;
    limit?: number | null;
  };
}): string => {
  const searchParamsBackend = convertSearchParamsToBackend({
    searchParams,
    globalRoomTypesFilter: filterParams?.roomTypes,
    globalBoardTypesFilter: filterParams?.boardTypes,
  });
  const filterParamsBackend = convertHotelFilterParamsToBackend(filterParams);
  const sortParamsBackend = sortParams
    ? convertSortParamsToBackend(sortParams)
    : null;

  const searchString =
    convertSearchParamsBackendToParamsString(searchParamsBackend);
  const filterString =
    convertFilterParamsBackendToParamsString(filterParamsBackend);
  const sortString =
    sortParams && sortParamsBackend
      ? convertSortParamsBackendToParamsString(sortParamsBackend)
      : '';
  const otherParamsString = convertOtherParamsBackendToParamsString({
    ...otherParams,
  });

  return [searchString, filterString, sortString, otherParamsString]
    .filter(Boolean)
    .join('&');
};

export const combineOfferParamsToBackendQuery = ({
  searchParams,
  filterParams = null,
  sortParams = null,
}: {
  searchParams: SearchParams;
  filterParams?: null | FilterParamsOffers;
  sortParams?: null | SortParams;
}): string => {
  let filterParamsBackend = convertOfferFilterParamsToBackend(filterParams);
  const sortParamsBackend = sortParams
    ? convertSortParamsToBackend(sortParams)
    : null;

  let searchParamsBackend = convertSearchParamsToBackend({
    searchParams,
    globalRoomTypesFilter: filterParams?.roomTypes,
    globalBoardTypesFilter: filterParams?.boardTypes,
  });

  // `filterPriceChart` behaves like a filter but is partially sent via searchParams
  if (filterParams?.filterPriceChart) {
    const {startDate, endDate, duration} = filterParams.filterPriceChart;

    searchParamsBackend = {
      ...searchParamsBackend,
      dateMin: startDate,
      dateMax: endDate,
    };
    filterParamsBackend = {
      ...filterParamsBackend,
      durationFilter: duration,
    };
  }

  const searchString =
    convertSearchParamsBackendToParamsString(searchParamsBackend);
  const sortString =
    sortParams && sortParamsBackend
      ? convertSortParamsBackendToParamsString(sortParamsBackend)
      : '';
  const filterString =
    filterParams && filterParamsBackend
      ? convertFilterParamsBackendToParamsString(filterParamsBackend)
      : '';

  return [searchString, filterString, sortString].filter(Boolean).join('&');
};
